/**
 * SecurityActivity — Phase 9.1 dashboard.
 *
 * Admin-only feed of security-relevant events (login success/failure,
 * password resets, permission denies, MFA lifecycle). Provides:
 *   • 7-day summary tiles (success/failure ratio, denies, MFA enrolments)
 *   • Filterable + paginated event feed
 *   • Human-readable severity + IP + UA columns
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Shield, RefreshCw, CheckCircle2, XCircle, Lock, KeyRound, Loader2, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SEV = {
  info:     'bg-slate-500/10 text-slate-700 border-slate-500/30',
  warning:  'bg-amber-500/10 text-amber-700 border-amber-500/30',
  critical: 'bg-red-500/10 text-red-700 border-red-500/30',
};

const EVENT_LABEL = {
  'auth.login.success':         { label: 'Login success',    tone: 'text-emerald-600' },
  'auth.login.failure':         { label: 'Login FAILED',     tone: 'text-red-600' },
  'auth.register.success':      { label: 'Registration',     tone: 'text-emerald-600' },
  'auth.register.failure':      { label: 'Registration failed', tone: 'text-red-600' },
  'auth.logout':                { label: 'Logout',           tone: 'text-muted-foreground' },
  'auth.password.reset_requested': { label: 'Password reset requested', tone: 'text-amber-600' },
  'auth.password.reset_completed': { label: 'Password reset completed', tone: 'text-emerald-600' },
  'auth.permission.denied':     { label: 'Permission denied', tone: 'text-orange-600' },
  'mfa.enrolled':               { label: 'MFA enrolled',     tone: 'text-emerald-600' },
  'mfa.disabled':               { label: 'MFA disabled',     tone: 'text-red-600' },
  'session.revoked':            { label: 'Session revoked',  tone: 'text-red-600' },
};

const EVENT_FILTER_OPTIONS = [
  { value: '__all__', label: 'All event types' },
  { value: 'auth.login.success', label: 'Login success' },
  { value: 'auth.login.failure', label: 'Login failure' },
  { value: 'auth.register.success', label: 'Register success' },
  { value: 'auth.permission.denied', label: 'Permission denied' },
  { value: 'auth.password.reset_requested', label: 'Password reset requested' },
];

export default function SecurityActivity() {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event_type: '', user_email: '', severity: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.severity) params.severity = filters.severity;
      const [s, e] = await Promise.all([
        api.get('/security/summary'),
        api.get('/security/events', { params }),
      ]);
      setSummary(s.data);
      setEvents(e.data?.events || []);
      setTotal(e.data?.total || 0);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load security data';
      toast.error(msg);
    } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const t = summary?.totals;
  const successRate = t?.logins_ratio_pct;

  const tiles = useMemo(() => [
    { label: 'Login success', value: t?.logins_success ?? 0, icon: CheckCircle2, tone: 'text-emerald-600', bg: 'bg-emerald-500/10' },
    { label: 'Login failure', value: t?.logins_failure ?? 0, icon: XCircle,       tone: 'text-red-600',     bg: 'bg-red-500/10' },
    { label: 'Perm denies',   value: t?.permission_denies ?? 0, icon: Lock,        tone: 'text-orange-600',  bg: 'bg-orange-500/10' },
    { label: 'MFA enrolled',  value: t?.mfa_enrolled ?? 0, icon: KeyRound,        tone: 'text-indigo-600',  bg: 'bg-indigo-500/10' },
  ], [t]);

  return (
    <div className="p-6 pb-32 max-w-7xl mx-auto space-y-6" data-testid="security-activity-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-600" />
            <h1 className="text-2xl font-heading font-bold" data-testid="security-activity-title">
              Security Activity
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Auth funnel · permission denies · session lifecycle · last 7 days rolling
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="security-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="security-summary">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Card key={tile.label} className="p-4" data-testid={`security-tile-${tile.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{tile.label}</div>
                  <div className="text-2xl font-heading font-bold mt-1">{tile.value}</div>
                </div>
                <div className={`w-10 h-10 rounded-lg ${tile.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${tile.tone}`} />
                </div>
              </div>
              {tile.label === 'Login success' && successRate != null && (
                <div className="mt-2 text-[11px] text-muted-foreground">
                  {successRate}% success rate
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Event type</label>
            <Select value={filters.event_type || '__all__'}
                    onValueChange={(v) => setFilters({ ...filters, event_type: v === '__all__' ? '' : v })}>
              <SelectTrigger data-testid="security-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                {EVENT_FILTER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">User email (exact)</label>
            <Input value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
                   placeholder="user@example.com" data-testid="security-filter-email" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Severity</label>
            <Select value={filters.severity || '__all__'}
                    onValueChange={(v) => setFilters({ ...filters, severity: v === '__all__' ? '' : v })}>
              <SelectTrigger data-testid="security-filter-severity"><SelectValue placeholder="All severities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All severities</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button className="w-full" onClick={load} disabled={loading} data-testid="security-apply-filter">
              <Search className="w-3.5 h-3.5 mr-1" /> Apply
            </Button>
          </div>
        </div>
      </Card>

      {/* Events */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <div className="font-heading font-semibold text-sm">Event feed</div>
            <div className="text-[11px] text-muted-foreground">
              {loading ? 'Loading…' : `${events.length} of ${total} shown · newest first`}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading events…
          </div>
        ) : events.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="security-events-empty">
            No security events recorded in this window.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="security-events-table">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">When</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">Event</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">User</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">IP</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">Severity</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {events.map((ev) => {
                  const meta = EVENT_LABEL[ev.event_type] || { label: ev.event_type, tone: '' };
                  return (
                    <tr key={ev.id} className="hover:bg-muted/30" data-testid={`security-event-row-${ev.id}`}>
                      <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(ev.created_at).toLocaleString()}
                      </td>
                      <td className={`px-4 py-2 font-medium text-xs ${meta.tone}`}>{meta.label}</td>
                      <td className="px-4 py-2 text-xs">{ev.user_email || '—'}</td>
                      <td className="px-4 py-2 text-[11px] font-mono text-muted-foreground">{ev.ip_address || '—'}</td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className={`text-[10px] ${SEV[ev.severity] || SEV.info}`}>
                          {ev.severity || 'info'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-[11px] text-muted-foreground truncate max-w-[280px]">
                        {ev.metadata?.reason
                          || ev.metadata?.detail
                          || (ev.user_agent
                                ? (ev.user_agent.length > 40 ? `${ev.user_agent.slice(0, 40)}…` : ev.user_agent)
                                : '—')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
